/**
 * PatientRewardsTab — staff-facing "Rewards" tab inside PatientDetailModal.
 * Shows a patient's full step-points wallet (steps · grants · spent ·
 * available), a manual grant/deduct form with required reason, the
 * redemption history with coupon codes, and the manual grant log with
 * void-button per row.
 *
 * Endpoints used:
 *   GET    /api/stepathon/admin/users/{id}/wallet
 *   POST   /api/stepathon/admin/users/{id}/grant
 *   DELETE /api/stepathon/admin/users/{id}/grant/{grant_id}
 */
import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
import { Gift, Plus, Minus, Trash2, Loader2, Footprints, Award } from "lucide-react";

const REDEMPTION_PILL = {
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
  fulfilled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

const PRESETS = [1000, 5000, 10000, 25000];

function StatTile({ label, value, accent, icon: Icon, testId }) {
  return (
    <div data-testid={testId}
         className={`rounded-2xl border ${accent} p-4 flex flex-col gap-1`}>
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold opacity-80">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-2xl font-black tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

export default function PatientRewardsTab({ patientId }) {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pointsInput, setPointsInput] = useState("");
  const [reason, setReason] = useState("");
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/stepathon/admin/users/${patientId}/wallet`);
      setWallet(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wallet");
    } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (sign) => {
    const n = parseInt(pointsInput, 10);
    if (!n || isNaN(n)) { toast.error("Enter a point amount"); return; }
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Reason is required (audit trail)"); return;
    }
    const signed = sign === "deduct" ? -Math.abs(n) : Math.abs(n);
    setGranting(true);
    try {
      const { data } = await API.post(
        `/stepathon/admin/users/${patientId}/grant`,
        { points: signed, reason: reason.trim() }
      );
      toast.success(
        sign === "deduct"
          ? `Deducted ${Math.abs(signed).toLocaleString()} pts · new balance ${data.points_available.toLocaleString()}`
          : `Granted ${signed.toLocaleString()} pts · new balance ${data.points_available.toLocaleString()}`
      );
      setPointsInput(""); setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally { setGranting(false); }
  };

  const voidGrant = async (g) => {
    const ok = await brandedConfirm({
      title: "Void this grant?",
      description: `This reverses ${g.points.toLocaleString()} pts. The row stays for audit; balance recomputes immediately.`,
      confirmText: "Void grant", tone: "warn",
    });
    if (!ok) return;
    try {
      await API.delete(`/stepathon/admin/users/${patientId}/grant/${g.id}`);
      toast.success("Grant voided");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
    }
  };

  if (loading || !wallet) {
    return (
      <div data-testid="rewards-tab-loading" className="py-8 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading wallet…
      </div>
    );
  }

  return (
    <div data-testid="patient-rewards-tab" className="space-y-6">
      {/* Balance overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile
          label="Available"
          value={wallet.points_available}
          accent="border-purple-300 bg-purple-50 text-purple-900"
          icon={Gift}
          testId="rewards-tile-available"
        />
        <StatTile
          label="From steps"
          value={wallet.points_from_steps}
          accent="border-sky-200 bg-sky-50 text-sky-900"
          icon={Footprints}
          testId="rewards-tile-steps"
        />
        <StatTile
          label="From grants"
          value={wallet.points_from_grants}
          accent="border-amber-200 bg-amber-50 text-amber-900"
          icon={Award}
          testId="rewards-tile-grants"
        />
        <StatTile
          label="Spent"
          value={wallet.points_spent}
          accent="border-rose-200 bg-rose-50 text-rose-900"
          testId="rewards-tile-spent"
        />
      </div>

      {/* Grant / deduct */}
      <div className="rounded-2xl border border-purple-200 bg-purple-50/40 p-5">
        <h4 className="font-bold text-purple-900">
          Grant or deduct points
        </h4>
        <p className="text-xs text-purple-700/80 mt-1">
          Every adjustment is audit-logged. Reason is required.
        </p>
        <div className="mt-4 grid sm:grid-cols-[1fr,2fr] gap-3">
          <div className="space-y-2">
            <Input
              data-testid="grant-points-input"
              type="number"
              min="1"
              value={pointsInput}
              onChange={(e) => setPointsInput(e.target.value)}
              placeholder="Points (e.g. 5000)"
              className="rounded-xl"
            />
            <div className="flex flex-wrap gap-1">
              {PRESETS.map(p => (
                <button
                  key={p}
                  data-testid={`grant-preset-${p}`}
                  onClick={() => setPointsInput(String(p))}
                  className="text-[11px] px-2 py-1 rounded-md bg-white border border-purple-200 hover:bg-purple-100 text-purple-700"
                >
                  +{p.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
          <Textarea
            data-testid="grant-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (e.g. compensation for shipping delay, referral bonus, charity walk credit)"
            rows={2}
            className="rounded-xl"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            data-testid="grant-add-btn"
            onClick={() => submit("grant")}
            disabled={granting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> Add points
          </Button>
          <Button
            data-testid="grant-deduct-btn"
            onClick={() => submit("deduct")}
            disabled={granting}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
          >
            <Minus className="w-4 h-4 mr-1" /> Deduct points
          </Button>
        </div>
      </div>

      {/* Grant log */}
      <div>
        <h4 className="font-bold text-slate-900 mb-2">Manual grant log</h4>
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">When</th>
                <th className="px-3 py-2 font-semibold tabular-nums">Δ pts</th>
                <th className="px-3 py-2 font-semibold">Reason</th>
                <th className="px-3 py-2 font-semibold">By</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {wallet.grants.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                  No manual grants yet.
                </td></tr>
              )}
              {wallet.grants.map(g => (
                <tr key={g.id}
                    data-testid={`grant-row-${g.id}`}
                    className={`border-t border-slate-100 ${g.voided ? "opacity-50 line-through" : ""}`}>
                  <td className="px-3 py-2 text-xs">
                    {g.granted_at && new Date(g.granted_at).toLocaleString()}
                  </td>
                  <td className={`px-3 py-2 tabular-nums font-bold ${
                    g.points >= 0 ? "text-emerald-700" : "text-rose-700"
                  }`}>
                    {g.points >= 0 ? "+" : ""}{g.points.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-xs max-w-xs truncate" title={g.reason}>
                    {g.reason}
                  </td>
                  <td className="px-3 py-2 text-xs">{g.granted_by_name || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {!g.voided && (
                      <button
                        data-testid={`grant-void-${g.id}`}
                        onClick={() => voidGrant(g)}
                        className="text-rose-600 hover:text-rose-800"
                        title="Void grant"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Redemption history */}
      <div>
        <h4 className="font-bold text-slate-900 mb-2">Redemption history</h4>
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">When</th>
                <th className="px-3 py-2 font-semibold">Reward</th>
                <th className="px-3 py-2 font-semibold tabular-nums">Spent</th>
                <th className="px-3 py-2 font-semibold">Coupon</th>
                <th className="px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {wallet.redemptions.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                  No redemptions yet.
                </td></tr>
              )}
              {wallet.redemptions.map(r => (
                <tr key={r.id}
                    data-testid={`redemption-row-${r.id}`}
                    className="border-t border-slate-100">
                  <td className="px-3 py-2 text-xs">
                    {r.redeemed_at && new Date(r.redeemed_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <div>{r.reward_name}</div>
                    {r.amount_dollars > 0 && (
                      <div className="text-[10px] text-emerald-700 font-bold">
                        ${r.amount_dollars.toFixed(2)} discount
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{r.points_spent.toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.coupon_code || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-1 rounded-full border ${REDEMPTION_PILL[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
